import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiInstance, configureOktaApiInstance } from './apiInstance';
import { clearToken, saveToken, readSavedDebtorId } from '../utils/sessionStorage';

// Store on window to survive HMR module re-evaluation
const getOktaAuthInstance = (): any => (window as any).__oktaAuthInstance ?? null;

export const setOktaAuthInstance = (oktaAuth: any) => {
  (window as any).__oktaAuthInstance = oktaAuth;
};

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const refreshToken = async (): Promise<string> => {
  const oktaAuth = getOktaAuthInstance();
  if (!oktaAuth) {
    throw new Error('No oktaAuth instance available');
  }

  const tokenResponse = await oktaAuth.tokenManager.renew('accessToken');

  if (!tokenResponse) {
    throw new Error('Failed to renew token');
  }

  const newAccessToken = tokenResponse.accessToken;
  const debtorId = readSavedDebtorId();

  configureOktaApiInstance({ token: newAccessToken }, debtorId ? String(debtorId) : '');
  saveToken(newAccessToken);

  return newAccessToken;
};

const handleTokenRefresh = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
};

const handleRefreshFailure = async (error: any): Promise<never> => {
  // Don't redirect if the failure is just a missing oktaAuth instance
  if (error?.message === 'No oktaAuth instance available') {
    throw error;
  }

  clearToken();
  window.location.href = '/login';
  throw error;
};

const isTokenExpired = (): boolean => {
  const oktaAuth = getOktaAuthInstance();
  if (!oktaAuth) return false;

  try {
    const accessToken = oktaAuth.tokenManager.getTokensSync()?.accessToken;
    if (!accessToken?.expiresAt) return false;

    const bufferSeconds = 60;
    const now = Math.floor(Date.now() / 1000);
    return accessToken.expiresAt - bufferSeconds <= now;
  } catch {
    return false;
  }
};

const ensureValidToken = async (): Promise<void> => {
  if (isTokenExpired()) {
    try {
      await handleTokenRefresh();
    } catch (error) {
      return handleRefreshFailure(error);
    }
  }
};

const withTokenRefresh = async <T>(
  request: () => Promise<AxiosResponse<T>>,
): Promise<AxiosResponse<T>> => {
  await ensureValidToken();

  try {
    return await request();
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      try {
        await handleTokenRefresh();
        return await request();
      } catch (refreshError) {
        return handleRefreshFailure(refreshError);
      }
    }

    throw error;
  }
};

export const oktaGet = <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return withTokenRefresh(() => apiInstance.get<T>(url, config));
};

export const oktaPost = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return withTokenRefresh(() => apiInstance.post<T>(url, data, config));
};

export const oktaPut = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return withTokenRefresh(() => apiInstance.put<T>(url, data, config));
};

export const oktaPatch = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return withTokenRefresh(() => apiInstance.patch<T>(url, data, config));
};

export const oktaDelete = <T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return withTokenRefresh(() => apiInstance.delete<T>(url, config));
};